import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { 
  likeItem, 
  unlikeItem, 
  checkLikeStatus, 
  getLikesCount, 
  getUsersWhoLiked, 
  getUserLikes, 
  syncLikesCounts 
} from '../api/likesApi';

// Hook to check if the current user has liked an item
export const useLikeStatus = (item_type, item_id) => {
  const { setError } = useAuthStore();

  return useQuery(
    ['likeStatus', item_type, item_id],
    () => checkLikeStatus({ item_type, item_id }),
    {
      onError: (error) => {
        setError(error?.response?.data?.message || 'Failed to check like status');
      },
      retry: false,
      enabled: !!(item_type && item_id), // Only run query if both parameters are provided
      staleTime: 60000, // 1 minute
    }
  );
};

// Hook to get likes count for an item
export const useLikesCount = (item_type, item_id) => {
  const { setError } = useAuthStore();

  return useQuery(
    ['likesCount', item_type, item_id],
    () => getLikesCount({ item_type, item_id }),
    {
      onError: (error) => {
        setError(error?.response?.data?.message || 'Failed to fetch likes count');
      },
      retry: false,
      enabled: !!(item_type && item_id),
      staleTime: 60000, // 1 minute
    }
  );
};

// Hook to get users who liked an item
export const useUsersWhoLiked = (item_type, item_id, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  return useQuery(
    ['usersWhoLiked', item_type, item_id, page, limit],
    () => getUsersWhoLiked({ item_type, item_id, page, limit }),
    {
      onError: (error) => {
        setError(error?.response?.data?.message || 'Failed to fetch users who liked this item');
      },
      retry: false,
      enabled: !!(item_type && item_id),
      keepPreviousData: true, // Useful for pagination
      staleTime: 300000, // 5 minutes
    }
  );
};

// Hook to get items liked by a user
export const useUserLikes = (userId = null, item_type = null, page = 1, limit = 10) => {
  const { setError } = useAuthStore();
  const queryKey = ['userLikes', userId, item_type, page, limit];

  return useQuery(
    queryKey,
    () => getUserLikes({ userId, item_type, page, limit }),
    {
      onError: (error) => {
        setError(error?.response?.data?.message || 'Failed to fetch liked items');
      },
      retry: false,
      keepPreviousData: true, // Useful for pagination
      staleTime: 300000, // 5 minutes
    }
  );
};

// Hook to like an item
export const useLikeItem = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(likeItem, {
    onSuccess: (_, variables) => {
      toast.success('Item liked successfully!');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries(['likeStatus', variables.item_type, variables.item_id]);
      queryClient.invalidateQueries(['likesCount', variables.item_type, variables.item_id]);
      queryClient.invalidateQueries('userLikes');
      
      // Optimistically update like status
      queryClient.setQueryData(
        ['likeStatus', variables.item_type, variables.item_id], 
        { liked: true }
      );
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to like item';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

// Hook to unlike an item
export const useUnlikeItem = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(unlikeItem, {
    onSuccess: (_, variables) => {
      toast.success('Item unliked successfully!');
      
      // Invalidate relevant queries
      queryClient.invalidateQueries(['likeStatus', variables.item_type, variables.item_id]);
      queryClient.invalidateQueries(['likesCount', variables.item_type, variables.item_id]);
      queryClient.invalidateQueries('userLikes');
      
      // Optimistically update like status
      queryClient.setQueryData(
        ['likeStatus', variables.item_type, variables.item_id], 
        { liked: false }
      );
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to unlike item';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

// Combined hook for like/unlike functionality
export const useLikeActions = () => {
  const likeMutation = useLikeItem();
  const unlikeMutation = useUnlikeItem();

  return {
    likeItem: likeMutation.mutate,
    unlikeItem: unlikeMutation.mutate,
    isLikeLoading: likeMutation.isLoading,
    isUnlikeLoading: unlikeMutation.isLoading,
    isLikeError: likeMutation.isError,
    isUnlikeError: unlikeMutation.isError,
    likeError: likeMutation.error,
    unlikeError: unlikeMutation.error
  };
};

// Admin hook to sync likes counts
export const useSyncLikesCounts = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(syncLikesCounts, {
    onSuccess: () => {
      toast.success('Likes counts synchronized successfully!');
      
      // Invalidate all likes count queries
      queryClient.invalidateQueries({ queryKey: ['likesCount'] });
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to sync likes counts';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};